import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { deleteLp, listLps, type Lp } from "@/lib/api/lps";
import { useRole } from "@/lib/auth/use-role";

function LpsListInner() {
  const tenant = useTenant();
  const qc = useQueryClient();
  const { canWrite } = useRole(tenant.id);
  const {
    data: lps,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["lps", tenant.id],
    queryFn: () => listLps(tenant.id),
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteLp(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lps", tenant.id] }),
  });

  function onDelete(lp: Lp): void {
    const ok = window.confirm(
      `Delete ${lp.legal_name}? This also deletes its fund commitments. This cannot be undone.`,
    );
    if (!ok) return;
    del.mutate(lp.id);
  }

  return (
    <>
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">LPs</h1>
          <p className="text-sm text-neutral-400">
            Limited partners in <span className="text-neutral-200">{tenant.name}</span>.
          </p>
        </div>
        {canWrite ? (
          <Link
            to="/t/$slug/lps/new"
            params={{ slug: tenant.slug }}
            className="rounded bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-950 hover:bg-white"
          >
            New LP
          </Link>
        ) : null}
      </header>

      {del.error ? (
        <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {del.error.message}
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-neutral-500">Loading LPs…</p>
      ) : error ? (
        <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {error.message}
        </div>
      ) : !lps || lps.length === 0 ? (
        <div className="rounded border border-neutral-800 bg-neutral-900 p-6 text-center text-sm text-neutral-400">
          <p className="font-medium text-neutral-100">No LPs yet</p>
          <p className="mt-1">
            {canWrite
              ? "Create your first LP. They will be available to attach to funds."
              : "Your tenant has no LPs yet. Ask an owner or admin to add one."}
          </p>
          {canWrite ? (
            <Link
              to="/t/$slug/lps/new"
              params={{ slug: tenant.slug }}
              className="mt-3 inline-block rounded bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-950 hover:bg-white"
            >
              Create LP
            </Link>
          ) : null}
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-neutral-800">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-neutral-800 bg-neutral-900/50 text-[11px] uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-3 py-2">Legal name</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Domicile</th>
                {canWrite ? <th className="px-3 py-2 text-right">Actions</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-900">
              {lps.map((lp) => (
                <tr key={lp.id} className="hover:bg-neutral-900/50">
                  <td className="px-3 py-2.5 text-neutral-100">{lp.legal_name}</td>
                  <td className="px-3 py-2.5 text-neutral-400">{lp.lp_type ?? "—"}</td>
                  <td className="px-3 py-2.5 text-neutral-400">{lp.domicile ?? "—"}</td>
                  {canWrite ? (
                    <td className="px-3 py-2.5 text-right text-xs">
                      <Link
                        to="/t/$slug/lps/$lpId/edit"
                        params={{ slug: tenant.slug, lpId: lp.id }}
                        className="text-neutral-300 hover:text-neutral-100"
                      >
                        Edit
                      </Link>
                      <button
                        type="button"
                        onClick={() => onDelete(lp)}
                        disabled={del.isPending}
                        className="ml-3 text-red-400 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function LpsListPage() {
  return (
    <TenantLayout>
      <LpsListInner />
    </TenantLayout>
  );
}
